import type { WeeklyStats } from '../../../lib/weeklyApi';
import { useI18n } from '../../../contexts/I18nContext';
import { useWeeklyStatusColor } from './WeeklyStatusVisual';

interface Props {
  stats: WeeklyStats;
}

/**
 * The week's numbers, drawn like the Daily Summary's report tiles: one card
 * with a title and a row of quiet tiles, each number in its status colour.
 */
export function WeeklyStatsHeader({ stats }: Props) {
  const { t } = useI18n();
  const colorOf = useWeeklyStatusColor();
  const tiles = [
    { label: t('weekly.stats.totalChanged'), value: stats.totalChanged, color: undefined },
    { label: t('weekly.stats.done'), value: stats.done, color: colorOf('DONE') },
    { label: t('weekly.stats.inProgress'), value: stats.inProgress, color: colorOf('IN_PROGRESS') },
    {
      label: t('weekly.stats.newlyStarted'),
      value: stats.newlyStarted,
      color: colorOf('IN_REVIEW'),
    },
    { label: t('weekly.stats.blocked'), value: stats.blocked, color: colorOf('BLOCKED') },
  ];

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="font-semibold text-gray-800">{t('weekly.stats.title')}</h2>
      <div className="mt-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-5">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-md bg-gray-50 py-2">
            <div
              className={`text-lg font-bold tabular-nums ${tile.color ? '' : 'text-gray-800'}`}
              style={tile.color ? { color: tile.color } : undefined}
            >
              {tile.value}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">{tile.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
